// components/ValidationHelper.js - Компонент для обработки и отображения ошибок валидации

import React from 'react';
import { AlertCircle, CheckCircle, X } from 'lucide-react';

// Компонент для отображения ошибки поля
export const FieldError = ({ error }) => {
    if (!error) return null;

    // Обработка массива ошибок от сервера Laravel
    const errorText = Array.isArray(error) ? error[0] : error;

    return (
        <div className="mt-1 flex items-center text-sm text-red-600">
            <AlertCircle size={14} className="mr-1 flex-shrink-0" />
            <span>{errorText}</span>
        </div>
    );
};

// Компонент для отображения успешных сообщений
export const SuccessMessage = ({ message, onClose }) => {
    if (!message) return null;

    return (
        <div className="mb-4 p-4 rounded-lg border border-green-200 bg-green-50 flex items-start">
            <CheckCircle className="text-green-600 mr-3 flex-shrink-0 mt-0.5" size={16} />
            <div className="flex-1">
                <p className="text-green-800 text-sm">{message}</p>
            </div>
            {onClose && (
                <button
                    onClick={onClose}
                    className="text-green-600 hover:text-green-800 ml-2"
                >
                    <X size={16} />
                </button>
            )}
        </div>
    );
};

// Компонент для отображения ошибок
export const ErrorMessage = ({ message, onClose }) => {
    if (!message) return null;

    return (
        <div className="mb-4 p-4 rounded-lg border border-red-200 bg-red-50 flex items-start">
            <AlertCircle className="text-red-600 mr-3 flex-shrink-0 mt-0.5" size={16} />
            <div className="flex-1">
                <p className="text-red-800 text-sm">{message}</p>
            </div>
            {onClose && (
                <button
                    onClick={onClose}
                    className="text-red-600 hover:text-red-800 ml-2"
                >
                    <X size={16} />
                </button>
            )}
        </div>
    );
};

// Компонент для валидации поля в реальном времени
export const InputWithValidation = ({
                                        type = "text",
                                        name,
                                        value,
                                        onChange,
                                        onBlur,
                                        placeholder,
                                        label,
                                        required = false,
                                        error,
                                        success,
                                        disabled = false,
                                        className = "",
                                        validationRules = {},
                                        showValidation = false
                                    }) => {
    const [isTouched, setIsTouched] = React.useState(false);
    const [localError, setLocalError] = React.useState('');
    const [isValid, setIsValid] = React.useState(false);

    // Валидация поля
    const validateField = React.useCallback((fieldValue) => {
        if (!showValidation || !isTouched) return;

        let errorMsg = '';

        // Проверка обязательности
        if (required && !fieldValue.trim()) {
            errorMsg = `${label} обязательно для заполнения`;
        }
        // Проверка минимальной длины
        else if (validationRules.minLength && fieldValue.length < validationRules.minLength) {
            errorMsg = `${label} должно содержать минимум ${validationRules.minLength} символов`;
        }
        // Проверка email
        else if (validationRules.email && fieldValue && !/\S+@\S+\.\S+/.test(fieldValue)) {
            errorMsg = 'Введите корректный email';
        }
        // Проверка логина
        else if (validationRules.login && fieldValue && !/^[a-zA-Z0-9_-]+$/.test(fieldValue)) {
            errorMsg = 'Только английские буквы, цифры, дефис и подчеркивание';
        }

        setLocalError(errorMsg);
        setIsValid(!errorMsg && fieldValue.trim().length > 0);
    }, [label, required, validationRules, showValidation, isTouched]);

    // Валидация при изменении значения
    React.useEffect(() => {
        validateField(value);
    }, [value, validateField]);

    const handleBlur = (e) => {
        setIsTouched(true);
        if (onBlur) onBlur(e);
    };

    const displayError = error || localError;
    const hasError = !!displayError;
    const showSuccess = success || (showValidation && isValid && isTouched);

    return (
        <div className="space-y-1">
            {label && (
                <label className="block text-sm font-medium text-gray-700">
                    {label} {required && <span className="text-red-500">*</span>}
                </label>
            )}

            <div className="relative">
                <input
                    type={type}
                    name={name}
                    value={value}
                    onChange={onChange}
                    onBlur={handleBlur}
                    placeholder={placeholder}
                    disabled={disabled}
                    className={`
                        w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-primary focus:border-transparent transition-colors
                        ${hasError ? 'border-red-500 bg-red-50' :
                        showSuccess ? 'border-green-500 bg-green-50' :
                            'border-gray-300'}
                        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                        ${className}
                    `}
                />

                {/* Индикатор валидности */}
                {showValidation && isTouched && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        {hasError ? (
                            <AlertCircle className="text-red-500" size={16} />
                        ) : isValid ? (
                            <CheckCircle className="text-green-500" size={16} />
                        ) : null}
                    </div>
                )}
            </div>

            <FieldError error={displayError} />
        </div>
    );
};

// Хелпер для обработки ошибок от сервера
export const processServerErrors = (serverErrors) => {
    if (!serverErrors || typeof serverErrors !== 'object') {
        return {};
    }

    // Преобразуем ошибки Laravel в удобный формат
    const processedErrors = {};

    Object.keys(serverErrors).forEach(field => {
        const fieldErrors = serverErrors[field];
        if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
            processedErrors[field] = fieldErrors[0]; // Берем первую ошибку
        } else if (typeof fieldErrors === 'string') {
            processedErrors[field] = fieldErrors;
        }
    });

    return processedErrors;
};

// Хелпер для очистки ошибок
export const clearFieldError = (errors, fieldName, setErrors) => {
    if (errors[fieldName]) {
        const newErrors = { ...errors };
        delete newErrors[fieldName];
        setErrors(newErrors);
    }
};

// Компонент уведомления
export const NotificationToast = ({ type, message, duration = 5000, onClose }) => {
    const [isVisible, setIsVisible] = React.useState(true);

    React.useEffect(() => {
        if (duration > 0) {
            const timer = setTimeout(() => {
                setIsVisible(false);
                setTimeout(() => onClose && onClose(), 300);
            }, duration);

            return () => clearTimeout(timer);
        }
    }, [duration, onClose]);

    if (!isVisible) return null;

    return (
        <div className={`fixed top-4 right-4 max-w-md p-4 rounded-lg shadow-lg transition-all duration-300 z-50 ${
            type === 'success'
                ? 'bg-green-500 text-white'
                : type === 'error'
                    ? 'bg-red-500 text-white'
                    : 'bg-blue-500 text-white'
        }`}>
            <div className="flex items-start">
                {type === 'success' ? (
                    <CheckCircle className="mr-3 flex-shrink-0 mt-0.5" size={16} />
                ) : (
                    <AlertCircle className="mr-3 flex-shrink-0 mt-0.5" size={16} />
                )}
                <div className="flex-1">
                    <p className="text-sm font-medium">{message}</p>
                </div>
                <button
                    onClick={() => {
                        setIsVisible(false);
                        setTimeout(() => onClose && onClose(), 300);
                    }}
                    className="ml-3 text-white hover:text-gray-200"
                >
                    <X size={16} />
                </button>
            </div>
        </div>
    );
};

export default {
    FieldError,
    SuccessMessage,
    ErrorMessage,
    InputWithValidation,
    processServerErrors,
    clearFieldError,
    NotificationToast
};
